import { ReactNode, useState } from 'react';
import { useSubscription } from '@/hooks/useSubscription';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Crown, Plane, Sparkles } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

interface SubscriptionGateProps {
  feature: string;
  children: ReactNode;
  requiredTier?: 'premium_economy' | 'first_class';
}

export function SubscriptionGate({ feature, children, requiredTier = 'premium_economy' }: SubscriptionGateProps) {
  const { hasFeature, subscription } = useSubscription();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  if (hasFeature(feature)) {
    return <>{children}</>;
  }

  const handleUpgrade = async () => {
    setLoading(true);
    try {
      const priceId = requiredTier === 'first_class' 
        ? 'price_1QbBvzRtEJH5cGHO8EAWjBmb'
        : 'price_1QbBuXRtEJH5cGHOIptCTiDW';

      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { priceId }
      });

      if (error) throw error;
      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error('Failed to start checkout process');
    } finally {
      setLoading(false);
    }
  };

  const tierInfo = {
    premium_economy: {
      name: 'Premium Economy',
      icon: Plane,
      color: 'bg-cyan-500',
      features: ['Advanced packing lists', 'SIM recommendations', 'Transport finder', 'Language phrases', 'Health tracking']
    },
    first_class: {
      name: 'First Class',
      icon: Crown,
      color: 'bg-gradient-to-r from-yellow-400 to-teal-500',
      features: ['Calendar sync', 'AI assistant', 'Pro transport', 'Expense tracking', 'Custom templates', 'Offline mode', 'Share templates']
    }
  };

  const info = tierInfo[requiredTier];
  const Icon = info.icon;

  return (
    <Card className="border-dashed border-2 border-muted-foreground/30">
      <CardHeader className="text-center">
        <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
          <Icon className="h-6 w-6 text-muted-foreground" />
        </div>
        <CardTitle className="flex items-center justify-center gap-2">
          <Sparkles className="h-5 w-5 text-yellow-500" />
          Upgrade to {info.name}
        </CardTitle>
        <CardDescription>
          This feature requires {info.name} subscription
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <p className="text-sm font-medium">Included features:</p>
          <ul className="text-sm text-muted-foreground space-y-1">
            {info.features.map((feat, index) => (
              <li key={index} className="flex items-center gap-2">
                <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                {feat}
              </li>
            ))}
          </ul>
        </div>
        <div className="flex flex-col gap-2">
          <Badge variant="secondary" className="w-fit">
            Current: {subscription.subscription_tier.replace('_', ' ')}
          </Badge>
          <Button className="w-full" onClick={handleUpgrade} disabled={loading}>
            {loading ? 'Loading...' : 'Upgrade Now'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}